import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, HelpCircle, Map } from 'lucide-react';
import {
  Button,
  getBpmnEventIcon,
  lookupAssignee,
  type AssigneeLookup,
  type BpmnEventType
} from '@bliks-io/ui';
import type {
  AssigneeModel,
  EntityOrSuggestionRef,
  PopulatedExternalContact,
  PopulatedOrgFunction,
  PopulatedSystem,
  PopulatedUser,
  ProcessEventNode,
  StepActionType
} from '@bliks-io/contracts';
import { StepCard } from './StepCard';
import { EntityHeader } from './EntityHeader';
import { asEventNodeSource } from '@/lib/assignment-source';
import { resolveStepOutput } from '@/lib/review-modal-utils';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useCanReadOrgCatalogs } from '@/hooks/useCanReadOrgCatalogs';

/**
 * Recorded-step shape this preview reads. Kept lax (most fields optional,
 * `assignedTo` is `unknown` for tolerated shapes) so both the assignment-list
 * payload and the wider populated recording shape — which now carries an
 * `EntityOrSuggestionRef` `assignedTo` — can flow in. The internal `StepCard`
 * still normalizes whatever it receives.
 */
interface ReviewablePreviousStep {
  _id: string;
  actionType: StepActionType;
  generatedName?: string | null;
  generatedDescription?: string | null;
  task?: {
    taskDescription?: string;
    output?: string;
  };
  rules?: Array<{ _id?: string; output?: string }>;
  assignedTo?: unknown;
  assignedToModel?: AssigneeModel;
}

/**
 * Type guard: is the supplied value a populated user/team/system/contact
 * shape (carries `_id` plus name fields) rather than an unresolved
 * `{ type, id }` ref? Used to skip the assignee chip when we only have a ref.
 */
function isPopulatedAssignee(
  value: unknown
): value is PopulatedUser | PopulatedSystem | PopulatedOrgFunction | PopulatedExternalContact {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  if (typeof obj._id !== 'string') return false;
  // EntityOrSuggestionRef has `type` and `id` only — no `_id`.
  return !('type' in obj && 'id' in obj && !('_id' in obj));
}

/**
 * The previous step's `assignedTo` may already be a populated entity (the
 * assignment-list payload) or an unresolved `{ type, id }` ref (the wider
 * populated recording shape). Use populated shapes directly; resolve refs
 * against the org lists. Populated users have `deletedAt` stripped on the
 * wire — prefer the lookup entry so the card still gets the lifecycle
 * markers.
 */
function resolvePreviousAssignee(
  previousStepObject: ReviewablePreviousStep | null,
  assigneeLookup: AssigneeLookup
) {
  const resolved = isPopulatedAssignee(previousStepObject?.assignedTo)
    ? previousStepObject.assignedTo
    : lookupAssignee(
        previousStepObject?.assignedTo as EntityOrSuggestionRef | null | undefined,
        previousStepObject?.assignedToModel,
        assigneeLookup
      );
  if (resolved && previousStepObject?.assignedToModel === 'User') {
    return assigneeLookup.users?.get(resolved._id) ?? resolved;
  }
  return resolved;
}

/**
 * Right-side card frame shared by the assignee / unclear / placeholder
 * variants: header row, divider, "your process step" placeholder box.
 * Exported so `currentStepSlot` callers can render custom variants without
 * duplicating the frame (and its fixed dimensions).
 */
export function CurrentStepSlotCard({
  header,
  stepLabel
}: {
  header: ReactNode;
  stepLabel: string;
}) {
  return (
    <div className="bg-white w-fit border border-border rounded-lg p-3">
      {header}
      <div className="h-px w-full bg-border my-2" />
      <div className="min-w-[316px] w-full h-[147.12px] p-3 rounded-lg border border-border">
        <div className="flex items-center gap-1">
          <Circle className="h-3.5 w-3.5 fill-primary text-primary" />
          <div className="font-bold text-xs">{stepLabel}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * The start circle of a start-anchored assignment. A populated start event
 * node (issue 11) renders from its own fields; legacy 'START' rows (only
 * migration M2's orphan-repair stock still carries the marker, kept one
 * release past P6) degrade to the generic process-start label — the
 * initialEvent mirror they used to render from is gone.
 */
function StartEventCircle({ eventNode }: { eventNode: ProcessEventNode | null }) {
  const { t } = useTranslation();
  const eventType = eventNode?.eventType;
  const name = eventNode?.name;
  const EventIcon =
    eventType && eventType !== 'none' ? getBpmnEventIcon(eventType as BpmnEventType) : null;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative size-[42px] flex items-center justify-center">
        <Circle size={42} strokeWidth={1} fill="white" className="absolute text-fg-subtle" />
        {EventIcon && <EventIcon size={20} className="z-10 text-fg-secondary" strokeWidth={1.5} />}
      </div>
      <div className="text-sm text-center text-black max-w-[120px]">
        {name || t('processPage.ProcessStart')}
      </div>
    </div>
  );
}

interface PreviousStepPreviewProps {
  sourceProcessStep?: string | ReviewablePreviousStep | ProcessEventNode | null;
  assignedTo?: PopulatedUser | PopulatedSystem | PopulatedOrgFunction | PopulatedExternalContact;
  assignedToModel?: AssigneeModel;
  showOnlyCurrentStep?: boolean;
  showUnclearSuccessorWarning?: boolean;
  ruleIds?: string[];
  /** When provided, replaces the entire right-side card area. `assignedTo`,
   *  `assignedToModel`, and `showUnclearSuccessorWarning` are ignored — the
   *  caller is fully responsible for rendering whatever belongs on the right. */
  currentStepSlot?: ReactNode;
  /**
   * Opens the caller's process map on this step. Opt-in per call site: the
   * button shows only where a host may have a readable process and a map
   * dialog mounted to receive the click. The preview raises the intent and
   * nothing else — which process is drawn, and everything fetched for it, is
   * the host's business.
   */
  onShowInMap?: () => void;
  /** The host is still reading its process; draws the map button busy (and inert). */
  showInMapPending?: boolean;
}

/**
 * PreviousStepPreview Component
 *
 * Displays information about the previous step and the current assignment.
 * Shows horizontal layout with dotted background and connector between cards.
 *
 * Without the catalog reads ({@link useCanReadOrgCatalogs}) refs fall back to
 * the payload's populated shape. Both hooks are React Query consumers, so the component needs a
 * QueryClientProvider — its own test mocks them rather than mounting one.
 *
 * @example
 * ```tsx
 * <PreviousStepPreview
 *   sourceProcessStep={previousStep}
 *   assignedTo={currentUser}
 *   ruleIds={['rule-1']}
 * />
 * ```
 */
export function PreviousStepPreview({
  sourceProcessStep,
  assignedTo,
  assignedToModel,
  showOnlyCurrentStep = false,
  showUnclearSuccessorWarning = false,
  ruleIds,
  currentStepSlot,
  onShowInMap,
  showInMapPending
}: PreviousStepPreviewProps) {
  const { t } = useTranslation();
  const canReadOrgCatalogs = useCanReadOrgCatalogs();
  const assigneeLookup = useAssigneeLookup({ enabled: canReadOrgCatalogs });

  // Start anchors come in two eras: a populated start event node (issue 11)
  // rendering from its own fields, or the legacy 'START' marker (orphan
  // rows, kept one release past P6) rendering the generic start label.
  const eventNodeSource = asEventNodeSource(sourceProcessStep);
  const isFirstAssignment = sourceProcessStep === 'START' || eventNodeSource !== null;
  const previousStepObject =
    sourceProcessStep != null && typeof sourceProcessStep !== 'string' && !eventNodeSource
      ? (sourceProcessStep as ReviewablePreviousStep)
      : null;
  const hasPreviousStep = isFirstAssignment || previousStepObject !== null;

  const previousAssignee = resolvePreviousAssignee(previousStepObject, assigneeLookup);

  return (
    <div className="relative border border-border rounded-lg px-4 py-7 step-background">
      {onShowInMap && (
        // Overlaid rather than placed in the flow: the button must not move the
        // cards, because it disappears when the host's process read fails.
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="absolute right-2 top-2 bg-white"
          loading={showInMapPending}
          onClick={onShowInMap}
          title={t('processPage.showInProcess')}
          aria-label={t('processPage.showInProcess')}
        >
          <Map aria-hidden />
        </Button>
      )}
      <div className="flex items-center justify-center gap-0">
        {previousStepObject && (
          <StepCard
            step={{
              actionType: previousStepObject.actionType,
              generatedName: previousStepObject.generatedName,
              generatedDescription: previousStepObject.generatedDescription,
              task: previousStepObject.task,
              assignedTo: previousAssignee,
              assignedToModel: previousStepObject.assignedToModel
            }}
            output={resolveStepOutput(previousStepObject, ruleIds)}
            className="min-w-[300px]"
          />
        )}

        {isFirstAssignment && <StartEventCircle eventNode={eventNodeSource} />}

        {hasPreviousStep && !showOnlyCurrentStep && (
          <div className="border-b h-px w-[21px] border-[#D1D1D1]" />
        )}

        {currentStepSlot ?? (
          <>
            {assignedTo && !showOnlyCurrentStep && (
              <CurrentStepSlotCard
                header={<EntityHeader entity={assignedTo} entityType={assignedToModel} />}
                stepLabel={t('processPage.YourProcessStep')}
              />
            )}

            {showUnclearSuccessorWarning && !assignedTo && !showOnlyCurrentStep && (
              <CurrentStepSlotCard
                header={
                  <div className="flex items-center gap-2 text-warning min-w-[200px]">
                    <HelpCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('myTasks.unclearResolution')}</span>
                  </div>
                }
                stepLabel={t('myTasks.processStep')}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
